"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function MarkerUnderline({ delay = 0.6 }: { delay?: number }) {
    return (
        <svg
            className="absolute left-0 -bottom-1 w-full pointer-events-none"
            height="12"
            viewBox="0 0 200 12"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <motion.path
                d="M2,7 C34,3 58,10 92,5 C118,1 150,9 198,6"
                stroke={LIME}
                strokeWidth="6"
                strokeLinecap="round"
                fill="none"
                opacity="0.7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.7, delay, ease: 'easeOut' }}
            />
        </svg>
    );
}

function RoughCircle() {
    return (
        <svg className="absolute -inset-2.5 pointer-events-none" viewBox="0 0 220 44" fill="none" aria-hidden="true">
            <motion.path
                d="M14,5 C70,-2 160,-1 205,10 C218,15 214,32 180,38 C120,46 40,44 12,34 C-2,29 2,10 14,5 Z"
                stroke={PURPLE}
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.85 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 1.05, ease: 'easeInOut' }}
            />
        </svg>
    );
}

function CurlyArrow() {
    return (
        <svg width="44" height="50" viewBox="0 0 44 50" fill="none" aria-hidden="true" className="shrink-0">
            <motion.path
                d="M38,4 C33,20 20,32 6,38"
                stroke={PURPLE}
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1.3 }}
            />
            <motion.path
                d="M2,28 L6,38 L16,36"
                stroke={PURPLE}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 1.85 }}
            />
        </svg>
    );
}

function StarRow({ rating }: { rating: number }) {
    return (
        <div className="flex items-center gap-0.5" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} width="9" height="9" viewBox="0 0 12 12" fill={i < rating ? PURPLE : 'rgba(8,4,0,0.15)'}>
                    <path d="M6 0l1.8 3.7 4.2.6-3 3 .7 4.2L6 9.5 2.3 11.5l.7-4.2-3-3 4.2-.6z" />
                </svg>
            ))}
        </div>
    );
}

function PinIcon({ size = 16 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M12 22s7-7.4 7-12.5A7 7 0 1 0 5 9.5C5 14.6 12 22 12 22z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <circle cx="12" cy="9.5" r="2.4" stroke="currentColor" strokeWidth="1.8" />
        </svg>
    );
}

const listings = [
    { name: 'Your Business', rating: 5, meta: 'Local favourite · 0.4 km', you: true },
    { name: 'Competitor A', rating: 4, meta: 'Nearby · 0.6 km' },
    { name: 'Competitor B', rating: 4, meta: 'Nearby · 0.9 km' },
];

function MapPanel() {
    return (
        <div className="relative w-full max-w-[380px] mx-auto lg:mx-0">
            {/* washi tape */}
            <div
                className="absolute -top-4 left-10 w-16 h-7 pointer-events-none"
                style={{ background: 'rgba(206,255,26,0.55)', transform: 'rotate(-6deg)', boxShadow: '0 2px 6px rgba(8,4,0,0.08)' }}
            />

            <motion.div
                initial={{ opacity: 0, y: 24, rotate: 0 }}
                whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.35, ease: 'easeOut' }}
                className="relative rounded-[22px] p-5"
                style={{ background: CARD, border: '1.5px solid rgba(8,4,0,0.1)', boxShadow: '0 24px 60px -20px rgba(8,4,0,0.25)' }}
            >
                {/* letterhead */}
                <div className="flex items-center justify-between pb-3.5 mb-3.5" style={{ borderBottom: '1px dashed rgba(8,4,0,0.14)' }}>
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: INK, color: LIME, fontFamily: CLASH }}>
                            LW
                        </div>
                        <div className="flex flex-col leading-none">
                            <span className="text-xs font-bold" style={{ fontFamily: CLASH, color: INK }}>Map Pack</span>
                            <span className="text-[11px] font-mono mt-0.5" style={{ color: 'rgba(8,4,0,0.4)' }}>near you</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <motion.span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: PURPLE }}
                            animate={{ opacity: [1, 0.35, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: 'rgba(8,4,0,0.4)' }}>live</span>
                    </div>
                </div>

                {/* mini map */}
                <div
                    className="relative rounded-2xl overflow-hidden mb-4"
                    style={{
                        height: 130,
                        background: 'linear-gradient(135deg, rgba(55,48,163,0.06), rgba(206,255,26,0.08))',
                    }}
                >
                    <div
                        className="absolute inset-0 opacity-40"
                        style={{
                            backgroundImage:
                                'repeating-linear-gradient(0deg, rgba(55,48,163,0.12) 0px, rgba(55,48,163,0.12) 1px, transparent 1px, transparent 26px), repeating-linear-gradient(90deg, rgba(55,48,163,0.12) 0px, rgba(55,48,163,0.12) 1px, transparent 1px, transparent 26px)',
                        }}
                    />
                    {/* competitor pins */}
                    <div className="absolute text-[rgba(8,4,0,0.3)]" style={{ left: '20%', top: '28%' }}>
                        <PinIcon size={16} />
                    </div>
                    <div className="absolute text-[rgba(8,4,0,0.3)]" style={{ left: '76%', top: '58%' }}>
                        <PinIcon size={16} />
                    </div>
                    {/* your pin, pulsing */}
                    <div className="absolute" style={{ left: '46%', top: '38%' }}>
                        <span className="relative flex items-center justify-center">
                            <motion.span
                                className="absolute rounded-full"
                                style={{ width: 34, height: 34, background: 'rgba(55,48,163,0.25)' }}
                                animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
                            />
                            <span style={{ color: PURPLE }}>
                                <PinIcon size={22} />
                            </span>
                        </span>
                    </div>
                </div>

                {/* listing rows */}
                <div className="flex flex-col gap-1">
                    {listings.map((l) => (
                        <div key={l.name} className="relative">
                            <div
                                className="flex items-center justify-between py-2 px-2.5 rounded-lg"
                                style={{ background: l.you ? 'rgba(206,255,26,0.16)' : 'transparent' }}
                            >
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-[13px] font-semibold" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.85)' }}>
                                        {l.name}
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        <StarRow rating={l.rating} />
                                        <span className="text-[10px]" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.4)' }}>{l.meta}</span>
                                    </div>
                                </div>
                            </div>
                            {l.you && <RoughCircle />}
                        </div>
                    ))}
                </div>

                <p className="sm:hidden text-center mt-2 rotate-[-1deg] font-semibold text-sm" style={{ fontFamily: MANROPE, color: PURPLE }}>
                    ↑ the spot everyone wants
                </p>

                <div className="absolute -right-2 top-[164px] hidden sm:flex items-start" style={{ transform: 'translateX(62%)' }}>
                    <div className="rotate-[-4deg] shrink-0" style={{ marginTop: -6 }}>
                        <CurlyArrow />
                    </div>
                    <span className="whitespace-nowrap -ml-1 mt-1 font-semibold text-xs rotate-[-2deg] inline-block" style={{ fontFamily: MANROPE, color: PURPLE }}>
                        the spot everyone wants
                    </span>
                </div>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.5 }}
                className="text-center mt-5 rotate-[1deg] font-semibold text-sm tracking-[0.02em]"
                style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.55)' }}
            >
                found nearby, chosen locally
            </motion.p>
        </div>
    );
}

const channels = [
    { label: 'Search', icon: <PinIcon size={16} /> },
    { label: 'Maps', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 4l6 2 5-2v14l-5 2-6-2-5 2V6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M9 4v14M15 6v14" stroke="currentColor" strokeWidth="1.7" /></svg>
    ) },
    { label: 'Reviews', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 0l1.8 3.7 4.2.6-3 3 .7 4.2L6 9.5 2.3 11.5l.7-4.2-3-3 4.2-.6z" transform="translate(6 6)" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>
    ) },
];

export default function LocalSEOHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-36 pb-24 overflow-hidden"
                style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
            >
                {/* faint map-grid texture */}
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage:
                            'repeating-linear-gradient(0deg, rgba(55,48,163,1) 0px, rgba(55,48,163,1) 1px, transparent 1px, transparent 42px), repeating-linear-gradient(90deg, rgba(55,48,163,1) 0px, rgba(55,48,163,1) 1px, transparent 1px, transparent 42px)',
                    }}
                />

                {/* single asymmetric wash */}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: '-18%', right: '-10%', width: 620, height: 560,
                        background: 'radial-gradient(circle, rgba(55,48,163,0.12) 0%, transparent 68%)',
                        borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                    }}
                />

                <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                    {/* Left: copy */}
                    <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/search-visibility" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                SEO Services
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>Local SEO</span>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 14, rotate: 0 }}
                            animate={{ opacity: 1, y: 0, rotate: -2 }}
                            transition={{ duration: 0.5, delay: 0.05 }}
                            className="text-xs md:text-sm font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
                            style={{ color: PURPLE, fontFamily: CLASH, border: `1.5px solid ${PURPLE}` }}
                        >
                            Local SEO
                        </motion.p>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-5xl lg:text-[3.3rem] font-bold leading-[1.08] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            Local SEO That Helps{' '}
                            <span className="relative inline-block" style={{ color: PURPLE }}>
                                Customers Find You
                                <MarkerUnderline delay={0.75} />
                            </span>{' '}
                            When It Matters
                        </motion.h1>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-4 max-w-xl"
                        >
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                When someone searches for a business near them, visibility is only useful if it leads to action.
                            </p>
                            <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Lead Workz helps businesses strengthen their local search presence across Google Search, Maps and their website by connecting location, services, reputation and customer intent. We build local SEO around the searches that can actually bring customers through your door.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.32 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                                style={{ background: PURPLE, color: '#ffffff', fontFamily: CLASH, letterSpacing: '0.08em' }}
                            >
                                Build My Local SEO Strategy →
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="flex items-center gap-4 pt-3"
                        >
                            <span className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.35)' }}>
                                Optimised across
                            </span>
                            <div className="flex items-center gap-2">
                                {channels.map((c) => (
                                    <div
                                        key={c.label}
                                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors duration-300"
                                        style={{ border: '1px solid rgba(8,4,0,0.12)', color: 'rgba(8,4,0,0.5)' }}
                                    >
                                        {c.icon}
                                        <span className="text-[11px] font-medium" style={{ fontFamily: MANROPE }}>{c.label}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: map + local pack illustration */}
                    <div className="lg:col-span-5">
                        <MapPanel />
                    </div>

                </div>
            </div>
        </section>
    );
}
